import classNames from "classnames";
import {
  Children,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent,
  ReactNode,
  cloneElement,
  isValidElement,
  useEffect,
  useId,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import TalaeraSpinner from "components/TalaeraSpinner/TalaeraSpinner";
import { getErrorComponents } from "services/util";
import { trapTabKey } from "utils/a11y";
import styles from "./index.module.scss";

type DialogProps = {
  open: boolean;
  /** Required unless hideHeader — the header renders it and labels the dialog. */
  title?: ReactNode;
  onClose: () => void;
  /**
   * Chromeless variant: no header bar, no kit ✕ — for dialogs whose body owns
   * the whole composition (onboarding steps). Pair with `aria-label`, and with
   * closeOnOverlayClick={false} when the body provides the only dismissal.
   */
  hideHeader?: boolean;
  /** Accessible name when there is no visible title (hideHeader). */
  "aria-label"?: string;
  /** Right-aligned footer actions — typically a GhostButton cancel + a ui/Button primary. */
  footer?: ReactNode;
  /**
   * Leading footer slot: rendered as the footer's first child, pushed to the
   * far left (the old .form-actions space-between layout). Use for
   * back/cancel/tertiary actions that anchor left while `footer` groups right.
   */
  footerLeading?: ReactNode;
  /**
   * Renders the kit inline spinner (.dialog-inline-spinner + TalaeraSpinner)
   * after the children. Purely visual — it disables nothing; pair with
   * disabled props on the footer actions where needed.
   */
  loading?: boolean;
  /**
   * When truthy, renders getErrorComponents(error) at the top of the body in
   * a polite live region. Accepts everything getErrorComponents does: API
   * error responses, ApiRequestError, strings, ReactNodes. Falsy skips.
   */
  error?: unknown;
  size?: "sm" | "md" | "lg" | "xl";
  /** Set false for dialogs with unsaved input — disables overlay-click AND Escape. Defaults to true. */
  closeOnOverlayClick?: boolean;
  /**
   * Visually hides the dialog while keeping it (and its children) mounted.
   * Transition-era affordance: legacy in-tree dialogs paint below a portaled
   * kit dialog, so a kit dialog that opens a legacy follow-on suspends itself
   * while the follow-on is up — imperative refs into its children stay live.
   */
  suspended?: boolean;
  /**
   * Wizard-header variant: the title centers between mirrored edge slots
   * (back affordance left, close right) — the look the booking/availability
   * dialogs speak. The default (undefined) keeps the left-aligned title.
   */
  titleAlign?: "center";
  /**
   * Back affordance for the wizard header: renders an arrow button left of
   * the centered title. Without it a mirrored spacer keeps the title centered.
   * Only rendered with titleAlign="center".
   */
  onBack?: () => void;
  /**
   * Extra class on the panel — the styling hook for call-site css (calendar
   * skins, per-dialog layout). Keep panel-level styling here, not in the kit.
   */
  className?: string;
  children: ReactNode;
};

/**
 * The app's dialog shell (rounded panel on a soft scrim): role="dialog",
 * aria-modal, labelled by its title, Escape-to-close, body scroll lock,
 * focus trap, and focus restore to the opener on close.
 *
 * Promoted from adminUi (which now re-exports it) as merge rung #1 of the
 * One-Kit convergence — the AppDialog family's 140 call sites migrate here
 * opportunistically. The portal keeps the .admin-v2 scope class so legacy
 * markup rendered inside a dialog body still picks up the admin skin; that
 * class is inert outside the admin console and goes away with the skin.
 */
export default function Dialog(props: DialogProps) {
  const { open, onClose, closeOnOverlayClick } = props;
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open || closeOnOverlayClick === false) {
      return;
    }
    function handleKeyDown(evt: globalThis.KeyboardEvent) {
      if (evt.key === "Escape") {
        onClose();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose, closeOnOverlayClick]);

  // Lock body scroll while the dialog is open — scrolling inside the dialog
  // must not scroll the page behind it.
  useEffect(() => {
    if (!open) {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  // Move focus into the panel on open; hand it back to the opener on close.
  useEffect(() => {
    if (!open) {
      return;
    }
    const opener = document.activeElement;
    panelRef.current?.focus();
    return () => {
      if (opener instanceof HTMLElement && opener.isConnected) {
        opener.focus();
      }
    };
  }, [open]);

  if (!open) {
    return null;
  }

  function handleOverlayClick(evt: MouseEvent) {
    if (evt.target === evt.currentTarget && props.closeOnOverlayClick !== false) {
      onClose();
    }
  }

  // Keep Tab cycling inside the panel while the dialog is open.
  function handleTrapKeyDown(evt: ReactKeyboardEvent) {
    if (evt.key === "Tab") {
      trapTabKey(panelRef.current, evt);
    }
  }

  const centered = props.titleAlign === "center";

  return createPortal(
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- backdrop click-to-close supplements Escape; the overlay is deliberately not in the tab order.
    <div
      className={classNames(
        "admin-v2",
        styles.overlay,
        props.suspended && styles.suspended
      )}
      aria-hidden={props.suspended || undefined}
      onClick={handleOverlayClick}
    >
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- the keydown IS the focus trap; role="dialog" containers are the one place a div legitimately listens for Tab. */}
      <div
        ref={panelRef}
        tabIndex={-1}
        className={classNames(
          styles.dialog,
          props.size === "sm" && styles.sm,
          props.size === "lg" && styles.lg,
          props.size === "xl" && styles.xl,
          props.className
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby={props.hideHeader ? undefined : titleId}
        aria-label={props.hideHeader ? props["aria-label"] : undefined}
        onKeyDown={handleTrapKeyDown}
      >
        {!props.hideHeader && (
          <header className={classNames(styles.head, centered && styles.headCentered)}>
            {centered &&
              (props.onBack ? (
                <button
                  type="button"
                  className={styles.back}
                  onClick={props.onBack}
                  aria-label="Back"
                >
                  ←
                </button>
              ) : (
                // Mirrors the close button so the title stays optically centered.
                <span className={styles.edgeSpacer} aria-hidden />
              ))}
            <h3
              id={titleId}
              className={classNames(styles.title, centered && styles.titleCentered)}
            >
              {props.title}
            </h3>
            <button
              type="button"
              className={styles.close}
              onClick={onClose}
              aria-label="Close"
            >
              ✕
            </button>
          </header>
        )}
        <div className={styles.body}>
          {Boolean(props.error) && (
            <div role="status" aria-live="polite">
              {getErrorComponents(props.error)}
            </div>
          )}
          {props.children}
          {props.loading && (
            <div className="dialog-inline-spinner">
              <TalaeraSpinner />
            </div>
          )}
        </div>
        {(props.footer || props.footerLeading) && (
          <footer className={styles.foot}>
            {props.footerLeading && (
              <div className={styles.footLeading}>{props.footerLeading}</div>
            )}
            {props.footer}
          </footer>
        )}
      </div>
    </div>,
    document.body
  );
}

type DialogFieldProps = {
  label: ReactNode;
  required?: boolean;
  /** Helper line under the control. */
  hint?: ReactNode;
  children: ReactNode;
};

/** Labelled form row inside a Dialog body. The label is programmatically
 * associated with a single control child (screen readers, autofill, click
 * focus); multi-child rows keep the visual-only association. */
export function DialogField(props: DialogFieldProps) {
  const generatedId = useId();
  const childArray = Children.toArray(props.children);
  const single = childArray.length === 1 ? childArray[0] : null;
  const associable = isValidElement<{ id?: string }>(single);
  const controlId = associable ? (single.props.id ?? generatedId) : undefined;

  return (
    <div>
      <label className={styles.label} htmlFor={controlId}>
        {props.required && (
          <span className={styles.required} aria-hidden>
            *{" "}
          </span>
        )}
        {props.label}
      </label>
      {associable
        ? cloneElement(single, {
            id: controlId,
            "aria-required": props.required || undefined,
          } as Partial<{ id: string }>)
        : props.children}
      {props.hint && <div className={styles.hint}>{props.hint}</div>}
    </div>
  );
}

type DialogRowProps = {
  children: ReactNode;
};

/** Two-column row of DialogFields. */
export function DialogRow(props: DialogRowProps) {
  return <div className={styles.row}>{props.children}</div>;
}

type StickyActionsProps = {
  className?: string;
  children: ReactNode;
};

/**
 * Pins primary actions to the bottom of the dialog body's scrollport, so tall
 * body content scrolls under them instead of pushing them out of view. Use this
 * for wizard-style dialogs whose CTA lives in the scrolling body rather than
 * the Dialog `footer` prop.
 *
 * Containment constraint: place as a direct child of the dialog body (or of a
 * container that spans the body's full scroll height) — position:sticky cannot
 * escape its parent. A sticky bar nested in a container that ends right after
 * it has nothing to stick against and renders inert.
 */
export function StickyActions(props: StickyActionsProps) {
  return (
    <div className={classNames(styles.stickyActions, props.className)}>
      {props.children}
    </div>
  );
}
